import {DataStatus, FormSubmitHandler, showNotification} from 'platform/components';

import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';
import {isTrue} from 'ramda-adjunct';

import {selectTenant} from '@dms/api/core';
import {
  useCreateDocumentLimitDefinitionMutation,
  useGetDocumentLimitDefinitionQuery,
  useUpdateDocumentLimitDefinitionMutation,
} from '@dms/api/finance';
import {DocumentLimitDefinitionResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {ApiError, CurrencyCodeType, getApiDateString, parseDate, useNavigate} from 'shared/web';

import {DefinitionFormType} from '../utils/types';
import {emptyRestrictionItem} from '../utils/utils';
import {CashRegisterLimitForm} from './CashRegisterLimitForm';

export function DefinitionDetail() {
  const navigate = useNavigate();

  const {id} = useParams();
  const isCreating = isNil(id);
  const {
    data: limitDetail,
    isLoading,
    isError,
  } = useGetDocumentLimitDefinitionQuery({id: id ?? ''}, {skip: isCreating});

  const {data: {currency} = {}} = useSelector(selectTenant);

  const [updateDefinition] = useUpdateDocumentLimitDefinitionMutation();
  const [createDefinition] = useCreateDocumentLimitDefinitionMutation();

  const handleSubmit: FormSubmitHandler<DefinitionFormType> = async (
    formValues,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const values: Omit<DocumentLimitDefinitionResponseBody, 'id'> = {
      restrictions: formValues.restrictions.map((item) => ({
        ...item,
        amount: {
          amount: item.amount.amount,
          currency: formValues.currency,
        },
        validFrom: item.validFrom ? getApiDateString(item.validFrom) : '',
      })),
      system: false,
      branchId: formValues.branchId,
      currency: formValues.system ? null : formValues.currency,
    };

    if (!isCreating) {
      if (!limitDetail) {
        return;
      }

      await updateDefinition({id: limitDetail.id, updateDocumentLimitDefinitionRequestBody: values})
        .unwrap()
        .then(() => {
          showNotification.success(
            i18n.t('entity.cashRegisterLimits.notifications.definitionUpdated')
          );
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.cashLimits);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    } else {
      await createDefinition({createDocumentLimitDefinitionRequestBody: values})
        .unwrap()
        .then(() => {
          showNotification.success(
            i18n.t('entity.cashRegisterLimits.notifications.definitionCreated')
          );
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.cashLimits);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }
  };

  return (
    <DataStatus isError={!currency || isError} isLoading={isLoading}>
      <CashRegisterLimitForm
        defaultValues={
          limitDetail
            ? {
                branchId: limitDetail.branchId,
                currency: limitDetail.currency ?? '',
                system: limitDetail.system,
                restrictions: limitDetail.restrictions.map((restriction) => ({
                  ...restriction,
                  validFrom: parseDate(restriction.validFrom),
                })) ?? [emptyRestrictionItem(currency as CurrencyCodeType)],
              }
            : {
                branchId: null,
                currency: currency ?? '',
                restrictions: [emptyRestrictionItem(currency as CurrencyCodeType)],
                system: false,
              }
        }
        edit={!isCreating}
        isSystem={isTrue(limitDetail?.system)}
        onSubmit={handleSubmit}
        cancelRoute={settingsRoutes.cashLimits}
      />
    </DataStatus>
  );
}
